import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FileItem, FileLikeObject, FileUploader, FileUploaderOptions, ParsedResponseHeaders } from 'ng2-file-upload';
import { ApiService, CommonService, PhxToastService } from '../../index';
import { CodeValue, PhxConstants, PhxDocumentFileUploadConfiguration, PhxDocumentFileUploaderOptions, PhxDocumentFileUploadFileItemActionEventArg } from '../../model';
import { PhxLocalizationService } from '../../services/phx-localization.service';
import { PhxDocumentFileUploadResourceKeys } from './phx-document-file-upload.resource-keys';
import { uuid } from '../../utility/uuid';
import { CustomFileItem } from './model';
import HTTPResponseStatus = PhxConstants.HTTPResponseStatus;
import { PhxToastType } from '../phx-toast/phx-toast-types';
import * as moment from 'moment';

@Component({
  selector: 'app-phx-document-file-upload',
  templateUrl: './phx-document-file-upload.component.html',
  styleUrls: ['./phx-document-file-upload.component.less']
})
export class PhxDocumentFileUploadComponent implements OnInit, OnDestroy, OnChanges {
  @Input() configuration: PhxDocumentFileUploadConfiguration;
  @Input() fileUploaderOptions: PhxDocumentFileUploaderOptions;
  @Input() editable = true;
  @Input() showAddButton = true;
  @Input() docTypeList: CodeValue[] = [];
  @Input() funcValidation?: (queue: any) => Array<any>;
  @Input() insideModal;
  @Output() showUploader: EventEmitter<boolean> = new EventEmitter();
  @Output() completeAll: EventEmitter<boolean> = new EventEmitter();
  @Output() successItem = new EventEmitter<PhxDocumentFileUploadFileItemActionEventArg>();
  @Output() successOnEachItem = new EventEmitter<PhxDocumentFileUploadFileItemActionEventArg>();

  @Input() getCustomDataModel;
  @Input() isDisabled: boolean;

  @ViewChild('fileUploadModal') fileUploadModal: any;

  uploader: FileUploader;
  hasBaseDropZoneOver: boolean;
  validationMessages: Array<any>;
  private endpoint = '/command/postfile';
  private error: string;
  private url: string;
  private multiDocumentUploadBatchId: string;
  disableUploadButton: boolean = true;
  constructor(
    private commonService: CommonService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private localizationService: PhxLocalizationService,
    private phxToastService: PhxToastService
  ) {
    this.hasBaseDropZoneOver = false;
    this.setNewBatchId();
  }

  ngOnDestroy() {
    this.close(false);
  }

  ngOnInit() {
    this.initUploader();
    this.handleStartUploadButton();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fileUploaderOptions || changes.configuration) {
      this.initUploader();
      if (changes.configuration)
        {this.handleStartUploadButton();}
    }
  }

  handleStartUploadButton() {
    const customConfig = this.configuration?.customUiConfig;
    const hasNoRequiredDate = customConfig && customConfig?.objectDate?.isRequired && customConfig?.objectDate.value == null;
    const hasNoComment = customConfig && customConfig?.objectComment != null && customConfig?.objectComment?.isRequired
      && (customConfig?.objectComment?.value == null || customConfig?.objectComment?.value?.trim() === ''
        || (customConfig?.objectComment?.value?.length < customConfig?.objectComment.minlength
          || customConfig?.objectComment?.value?.length > customConfig?.objectComment.maxlength));

    if (hasNoRequiredDate || hasNoComment) {
      this.disableUploadButton = true;      
    }
    else {
      this.disableUploadButton = false;
    }
  }

  close(initUploaderAgain = true) {
    if (this.fileUploadModal) {
      this.fileUploadModal.hide();
    }
    if (initUploaderAgain) {
      this.initUploader();
    }
  }

  showModal(fileUploaderOptions: PhxDocumentFileUploaderOptions = null) {
    if (this.uploader && fileUploaderOptions !== null) {
      this.fileUploaderOptions = fileUploaderOptions;
      this.initUploader();
    }
    this.showUploader.emit();
    this.fileUploadModal.show();
  }

  fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  upload() {
    let valMessages: Array<any> = [];
    this.validationMessages = [];
    if (this.funcValidation) {
      valMessages = this.funcValidation(this.uploader.queue);
    }
    if (valMessages.length > 0) {
      this.validationMessages = valMessages;
    } else {
      this.uploadNextItem();
    }
  }

  discardDocument(document: CustomFileItem): void {
    if (document.isUnverified) {
      this.apiService.command('ComplianceDocumentSingleDiscard', { ComplianceDocumentId: document.complianceDocumentId, PublicId: document.publicId }, false);
    }
    document.remove();
    if (this.uploader.queue.length === 0) {
      this.uploader.clearQueue();
    }
  }

  continueDocument(document: CustomFileItem): void {
    document.isUnverified = false;
    document.remove();
    if (!this.areTemperedOrUnverifiedItems()) {
      this.successItem.emit({ item: document, response: document.response, status: HTTPResponseStatus.Ok });
      this.uploader.onCompleteAll();
    }
  }

  private areTemperedOrUnverifiedItems(): boolean {
    return this.uploader.queue.some((item: CustomFileItem) => {
      return item.isTampered || item.isUnverified;
    });
  }

  private setNewBatchId() {
    this.multiDocumentUploadBatchId = uuid.create();
    this.url = this.apiService.urlWithRoom(this.endpoint, this.multiDocumentUploadBatchId);
  }
  private initUploader() {
    this.error = '';
    const url: string = this.url;

    const uploaderOptions: FileUploaderOptions = {
      url,
      filters: [{
        name: 'minSize',
        fn: (item?: FileLikeObject, options?: FileUploaderOptions): boolean => {
          return item.size > 0;
        }
      }]
    };

    if (this.fileUploaderOptions) {
      this.applyUploaderOptions(uploaderOptions);
    }

    this.uploader = new FileUploader(uploaderOptions);

    this.uploader.onAfterAddingFile = (fileItem) => this.onAfterAddingFile(fileItem);

    this.uploader.onBeforeUploadItem = (fileItem) => this.onBeforeUploadItem(fileItem);

    this.uploader.onSuccessItem = (item: CustomFileItem, response, status, headers) => this.onSuccessItem(item, response, status, headers);

    this.uploader.onErrorItem = (item, response, status, headers) => this.onErrorItem(item, response, status);

    this.uploader.onWhenAddingFileFailed = (item, filter, options) => this.onWhenAddingFileFailed(item, filter);

    this.uploader.onCompleteAll = () => this.onCompleteAll();
  }

  private applyUploaderOptions(uploaderOptions: FileUploaderOptions) {
    if (this.fileUploaderOptions.maxFileSize > 0) {
      uploaderOptions.maxFileSize = this.fileUploaderOptions.maxFileSize;
    }

    if (this.fileUploaderOptions.queueLimit > 0) {
      uploaderOptions.queueLimit = this.fileUploaderOptions.queueLimit;
    }

    if (this.fileUploaderOptions.allowedMimeType?.length) {
      uploaderOptions.allowedMimeType = this.fileUploaderOptions.allowedMimeType;
    }

    if (this.fileUploaderOptions.allowedFileType?.length) {
      uploaderOptions.allowedFileType = this.fileUploaderOptions.allowedFileType;
    }
  }

  private onAfterAddingFile(item: FileItem) {
    this.trimFileName(item);
  }

  private onBeforeUploadItem(item: FileItem) {
    item.withCredentials = false;
    if (this.configuration == null) {
      this.error = `configuration property on phx-document-file-upload is null`;
      this.commonService.logError(this.error);
    }

    if (this.uploader.getIndexOfItem(item) === this.uploader.queue.length - 1) {
      this.configuration.isFinalDocument = true;
    }
    
    if (this.configuration.customUiConfig) {
      this.configuration.customDateTime = this.configuration.customUiConfig.objectDate?.value
        ? moment(this.configuration.customUiConfig.objectDate?.value).format('YYYY-MM-DD')
        : null;

      this.configuration.customComment = this.configuration.customUiConfig.objectComment?.value;

      this.configuration.documentTypeId =
        this.configuration.customUiConfig.objectDocumentType?.value ?? this.configuration.documentTypeId;

      this.configuration.customUiConfig = null;
    }

    const customJson = typeof this.getCustomDataModel !== 'undefined' ? this.getCustomDataModel() : {};
    this.uploader.options.additionalParameter = { ...this.configuration, customJson: JSON.stringify(customJson), multiDocumentUploadBatchId: this.multiDocumentUploadBatchId };
    this.setUploaderAuthToken();
    this.cdr.detectChanges();
  }

  private onSuccessItem(item: CustomFileItem, response: string, status: number, headers: ParsedResponseHeaders) {
    
    const responseJson = JSON.parse(response);
    const { CustomPayload: customPayload } = responseJson.commandResult;
    
    if (responseJson.publicId === '00000000-0000-0000-0000-000000000000') {
      this.logFileUploadErrors(responseJson);
    }
    else if (customPayload && !customPayload.AllowUploadingTamperedPDFs &&
      (customPayload.ESignedStatusId === PhxConstants.ESignedStatus.SignedTempered ||
        customPayload.ESignedStatusId === PhxConstants.ESignedStatus.SignedUnverified)
    ) {
      item.isSuccess = false;
      item.isError = true;
      item.isTampered = customPayload.ESignedStatusId === PhxConstants.ESignedStatus.SignedTempered;
      item.isUnverified = customPayload.ESignedStatusId === PhxConstants.ESignedStatus.SignedUnverified;
      item.complianceDocumentId = customPayload.ComplianceDocumentId;
      item.publicId = customPayload.PublicId;
      item.response = responseJson;
    }
    else {
      if (this.uploader.getIndexOfItem(item) === this.uploader.queue.length - 1 && !this.areTemperedOrUnverifiedItems()) {
        const customComment = this.uploader.options.additionalParameter.customComment;
        this.successItem.emit({ item, response: JSON.parse(response), status, customComment });
      }
      this.successOnEachItem.emit({ item, response: JSON.parse(response), status });
    }

    this.uploadNextItem();
  }

  private onErrorItem(item: FileItem, response: string, status: number) {
    if (status === HTTPResponseStatus.Unauthorized) {
      this.apiService.refreshToken().subscribe(() => {
        item.isUploaded = false;
        item.isUploading = false;
        this.uploadNextItem();
      });
    } else {
      const res = JSON.parse(response === '' ? '{}' : response);
      if (res && response) {
        this.error = JSON.stringify(res);
      } else {
        this.error = this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadErrorMessage, item.file.name, status, response);
      }
      this.commonService.logError(this.error);
      this.uploadNextItem();
    }
  }

  private onWhenAddingFileFailed(item: FileLikeObject, error: any) {
    switch (error.name) {
      case 'fileSize':
        this.error = this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadFailedFileSizeMessage,
          item.name, this.fileUploaderOptions.maxFileSize / 1024 / 1024);
        break;
      case 'minSize':
        this.error = this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.invalidFileUploaded);
        break;
      case 'mimeType':
      case 'fileType':
        this.error = this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadFailedFileTypeMessage);
        break;
      case 'queueLimit':
        this.error = this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadFailedQueueLimitMessage, this.fileUploaderOptions.queueLimit);
        break;
      default:
        this.error =
          item && item.name
            ? this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadFailedDefaultWithNameMessage, error.name, item.name)
            : this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadFailedDefaultMessage, error.name);
    }
    this.commonService.logError(this.error);
  }

  private onCompleteAll() {
    if (!this.anyFileFailedUpload()) {
      this.close();
      this.setNewBatchId();
      this.completeAll.emit();
    }
  }

  private anyFileFailedUpload() {
    return this.uploader.queue.some((x: CustomFileItem) => x.isError || x.isTampered || x.isUnverified);
  }

  private setUploaderAuthToken() {
    this.uploader.authToken = 'Bearer ' + this.commonService.bearerToken();
  }

  private uploadNextItem() {
    this.cdr.detectChanges();
    const items = this.uploader.getNotUploadedItems();
    if (items && Array.isArray(items) && items.length) {
      const item = items[0];
      this.uploader.uploadItem(item);
    } else {
      this.uploader.onCompleteAll();
    }
  }

  private trimFileName(item: FileItem) {
    if (item?.file?.name?.length) {
      const lastPointPosition = item.file.name.lastIndexOf('.');
      item.file.name = item.file.name.substring(0, lastPointPosition).trim() + item.file.name.substring(lastPointPosition);
    }
  }

  private logFileUploadErrors(responseJson: any) {

    const errors = this.commonService.parseResponseError(responseJson.commandResult);

    if (responseJson.exceptionMessage?.length) {
      this.commonService.logError(responseJson.exceptionMessage);
    }
    else if (errors.length) {
      let exceptionMessage = errors.map(error => (error.PropertyName ? `<strong>${error.PropertyName}</strong>: ` : '') + error.Message).join('<br />');

      if (errors.length > 1) {
        exceptionMessage = '<br />' + exceptionMessage;
      }

      this.phxToastService.showToast(`Failure for ${responseJson.documentName}: `, exceptionMessage, PhxToastType.danger, null, -1);
    }
    else {
      this.commonService.logError(this.localizationService.translate(PhxDocumentFileUploadResourceKeys.documentFileUploadComponent.uploadErrorRefreshListMessage));
    }
  }
}
